/**
 * The Central's routing rules (D638, doc 25 sections 7 and 14 phase 3) - governed key `central.routing`.
 *
 * ONE rule set per scope says, for a conversation that reached the Central, WHICH team function answers it (and how fast), so the
 * routing is data - versioned, effective-dated, four-eyes, audited, content-hashed - never code. Why a governed setting and not a
 * store of its own: the kernel already gives a rule set exactly what the doc asks of it (versions, effective dates, two people for
 * every change, rollback, a per-country bucket, a stable `policyVersion` string that every routing decision records), and the
 * Admin's governed-settings flow edits and reviews it. A parallel table would re-implement all of that.
 *
 * What a rule set holds:
 *  - `safety`: the triggers that send a contact to a PERSON of the always-open safety team, first, ahead of every rule below
 *    (a linked safety case, a SAFETY category, an urgent priority). A safety contact is never held for the assistant.
 *  - `rules`: ordered; the first whose criteria all hold wins. A rule names a function and 1+ criteria (audience, topic, support
 *    category, case kind, channel, language, market); several values of one criterion are alternatives, different criteria are all required.
 *  - `fallbackFunction`: where a contact no rule claims goes. `overflowFunction`: the outer ring of the bullseye.
 *  - `widening`: the "bullseye" - after N seconds with nobody taking it, the contact is offered to the next ring: the team of its
 *    market, then the country team, then the overflow team.
 *  - `reassign`: how long an assigned person may be offline, or silent, before the contact goes back to the queue.
 *  - `capacity`: how many conversations at once a person takes per channel by default, and the ceiling a person may set for themselves.
 *  - `sla`: first-response and resolution targets per priority (optionally per function and channel).
 *
 * A platform version is the default for every country; a country version replaces it WHOLE. The registry default is the standard
 * rule set below (the six default teams of doc 25 section 7 mapped to the topics of the widget and the case kinds), so a country
 * that publishes nothing is routed correctly. A version the reader cannot verify or parse fails to the BUILT-IN default, never to
 * "no routing" (`CENTRAL_ROUTING_INVALID_DEFAULTED`).
 *
 * Neutral: no country, currency or language is named (the audience / topic / category / case-kind keys are the data keys of the
 * entry channels; labels come from the vocabulary catalog). The function and channel lists mirror `@vello/central-teams` (a test in
 * `@vello/central-routing` pins the equality, so `@vello/config` stays free of Central packages).
 */
import { COUNTRY_RE, effectiveBucket, isObj, onlyMembers, scopedSetting } from './central-ai-shared.js';
/** The governed key. */
export const CENTRAL_ROUTING_KEY = 'central.routing';
/** Mirrors `TEAM_FUNCTIONS` of `@vello/central-teams` (pinned by a test). */
export const CENTRAL_ROUTING_FUNCTIONS = ['atendimento', 'financeiro', 'comercial', 'cadastro_prestadores', 'seguranca', 'supervisao'];
/** Mirrors `TEAM_CHANNELS` of `@vello/central-teams` (pinned by a test). */
export const CENTRAL_ROUTING_CHANNELS = ['whatsapp', 'web_widget', 'mobile_app', 'voice'];
/** Mirrors `CONVERSATION_PRIORITIES` of `@vello/service-center`, lowest first. */
export const CENTRAL_ROUTING_PRIORITIES = ['low', 'normal', 'high', 'urgent'];
/** The function a safety contact is routed to: always the always-open one, never chosen by a rule set. */
export const CENTRAL_ROUTING_SAFETY_FUNCTION = 'seguranca';
export const CENTRAL_ROUTING_BOUNDS = {
    maxRules: 80,
    maxValuesPerCriterion: 24,
    maxSlaEntries: 60,
    widenSeconds: { min: 0, max: 3_600 },
    safetyWidenSeconds: { min: 5, max: 600 },
    agentOfflineAfterSeconds: { min: 15, max: 3_600 },
    noReplyAfterSeconds: { min: 30, max: 7_200 },
    capacityDefault: { min: 1, max: 20 },
    capacityCeiling: { min: 1, max: 50 },
    firstResponseSeconds: { min: 5, max: 86_400 },
    resolutionSeconds: { min: 60, max: 2_592_000 },
};
const rule = (ruleId, fn, criteria) => ({ ruleId, function: fn, ...criteria });
/**
 * The standard rule set (doc 25 sections 7 and 14): the topics of the widget's audiences (D625), the support categories and the case
 * kinds mapped to the six default teams. Safety is not a topic: it is the `safety` triggers. A case counts by its KIND when it has one
 * (the D637 vocabulary `CASE_KINDS`: `dispute`, `cash_shortfall`, `occurrence`, `courier_incident`, `widget_case`, ...) and by its coarse
 * domain (`support`, `finance`, `safety`, `risk`) when an agent attached it before the one case model, so both name lists appear below.
 */
export const DEFAULT_CENTRAL_ROUTING = {
    rules: [
        rule('finance-cases', 'financeiro', { caseKinds: ['finance', 'risk', 'dispute', 'cash_shortfall'] }),
        rule('payment-topics', 'financeiro', { topics: ['payment', 'earnings', 'payout'] }),
        rule('payment-category', 'financeiro', { categories: ['PAYMENT'] }),
        rule('provider-registration-topics', 'cadastro_prestadores', { topics: ['document', 'signup', 'signup_status'] }),
        rule('provider-registration-category', 'cadastro_prestadores', { categories: ['DOCUMENT'] }),
        rule('candidates', 'cadastro_prestadores', { audiences: ['candidate'] }),
        rule('partner-topics', 'comercial', { topics: ['partnership', 'store'] }),
        rule('partner-category', 'comercial', { categories: ['MERCHANT'] }),
        rule('service-topics', 'atendimento', { topics: ['ride', 'food', 'send', 'lost_item', 'account', 'trip', 'rider'] }),
        rule('service-categories', 'atendimento', { categories: ['TRIP', 'LOST_ITEM', 'ACCOUNT', 'DRIVER', 'RIDER'] }),
        rule('service-cases', 'atendimento', { caseKinds: ['support', 'ticket', 'occurrence', 'courier_incident', 'food_complaint', 'send_claim', 'phone_change', 'widget_case'] }),
    ],
    safety: { caseKinds: ['safety', 'safety_incident'], categories: ['SAFETY'], topics: [], priorities: ['urgent'], widenAfterSeconds: 15 },
    fallbackFunction: 'atendimento',
    overflowFunction: 'atendimento',
    widening: { toCountryAfterSeconds: 60, toOverflowAfterSeconds: 180 },
    reassign: { agentOfflineAfterSeconds: 60, noReplyAfterSeconds: 180 },
    capacity: {
        defaults: { whatsapp: 3, web_widget: 3, mobile_app: 3, voice: 1 },
        ceilings: { whatsapp: 8, web_widget: 8, mobile_app: 8, voice: 2 },
    },
    sla: [
        { priority: 'urgent', firstResponseSeconds: 30, resolutionSeconds: 3_600 },
        { priority: 'high', firstResponseSeconds: 60, resolutionSeconds: 28_800 },
        { priority: 'normal', firstResponseSeconds: 120, resolutionSeconds: 86_400 },
        { priority: 'low', firstResponseSeconds: 600, resolutionSeconds: 259_200 },
        // A call is answered in seconds (doc 25 section 2: phone 80% in 20 s).
        { priority: 'urgent', channels: ['voice'], firstResponseSeconds: 20, resolutionSeconds: 3_600 },
        { priority: 'high', channels: ['voice'], firstResponseSeconds: 20, resolutionSeconds: 28_800 },
        { priority: 'normal', channels: ['voice'], firstResponseSeconds: 20, resolutionSeconds: 86_400 },
    ],
};
export const CENTRAL_ROUTING_REASON_CODES = ['CENTRAL_ROUTING_GOVERNED', 'CENTRAL_ROUTING_DEFAULT', 'CENTRAL_ROUTING_INVALID_DEFAULTED'];
const RULE_ID = /^[a-z][a-z0-9-]{1,39}$/;
const KEY = /^[a-z][a-z0-9_]{0,39}$/;
const CATEGORY = /^[A-Z][A-Z0-9_]{0,39}$/;
const LANGUAGE = /^[a-z]{2,3}$/;
const MARKET = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
const isIntIn = (v, b) => typeof v === 'number' && Number.isInteger(v) && v >= b.min && v <= b.max;
const oneOf = (list, v) => typeof v === 'string' && list.includes(v);
/** A list of 0..N distinct strings that each match `shape`; false otherwise. */
function distinctList(v, shape, min = 0) {
    return Array.isArray(v) && v.length >= min && v.length <= CENTRAL_ROUTING_BOUNDS.maxValuesPerCriterion && v.every(shape) && new Set(v).size === v.length;
}
const matches = (re) => (s) => typeof s === 'string' && re.test(s);
const RULE_MEMBERS = ['ruleId', 'function', 'audiences', 'topics', 'categories', 'caseKinds', 'channels', 'languages', 'markets'];
const CRITERIA = [
    { member: 'audiences', ok: matches(KEY) },
    { member: 'topics', ok: matches(KEY) },
    { member: 'categories', ok: matches(CATEGORY) },
    { member: 'caseKinds', ok: matches(KEY) },
    { member: 'channels', ok: (s) => oneOf(CENTRAL_ROUTING_CHANNELS, s) },
    { member: 'languages', ok: matches(LANGUAGE) },
    { member: 'markets', ok: matches(MARKET) },
];
function ruleIssue(r) {
    if (!isObj(r))
        return 'RULE_NOT_AN_OBJECT';
    if (!onlyMembers(r, RULE_MEMBERS))
        return 'RULE_UNKNOWN_MEMBER';
    if (typeof r.ruleId !== 'string' || !RULE_ID.test(r.ruleId))
        return 'RULE_ID_INVALID';
    if (!oneOf(CENTRAL_ROUTING_FUNCTIONS, r.function))
        return 'RULE_FUNCTION_INVALID';
    let criteria = 0;
    for (const c of CRITERIA) {
        if (!(c.member in r))
            continue;
        if (!distinctList(r[c.member], c.ok, 1))
            return 'RULE_CRITERION_INVALID';
        criteria += 1;
    }
    return criteria === 0 ? 'RULE_WITHOUT_CRITERIA' : undefined;
}
function safetyIssue(s) {
    if (!isObj(s))
        return 'SAFETY_NOT_AN_OBJECT';
    if (!onlyMembers(s, ['caseKinds', 'categories', 'topics', 'priorities', 'widenAfterSeconds']) || !('widenAfterSeconds' in s))
        return 'SAFETY_UNKNOWN_OR_MISSING_MEMBER';
    if (!distinctList(s.caseKinds, matches(KEY)) || !distinctList(s.categories, matches(CATEGORY)) || !distinctList(s.topics, matches(KEY)))
        return 'SAFETY_CRITERION_INVALID';
    if (!distinctList(s.priorities, (p) => oneOf(CENTRAL_ROUTING_PRIORITIES, p)))
        return 'SAFETY_PRIORITIES_INVALID';
    if (!isIntIn(s.widenAfterSeconds, CENTRAL_ROUTING_BOUNDS.safetyWidenSeconds))
        return 'SAFETY_WIDEN_INVALID';
    const empty = s.caseKinds.length + s.categories.length + s.topics.length + s.priorities.length === 0;
    return empty ? 'SAFETY_WITHOUT_TRIGGERS' : undefined;
}
function perChannel(v, b) {
    return isObj(v) && onlyMembers(v, CENTRAL_ROUTING_CHANNELS) && CENTRAL_ROUTING_CHANNELS.every((c) => isIntIn(v[c], b));
}
function slaIssue(list) {
    if (!Array.isArray(list) || list.length < CENTRAL_ROUTING_PRIORITIES.length || list.length > CENTRAL_ROUTING_BOUNDS.maxSlaEntries)
        return 'SLA_INVALID';
    const seen = new Set();
    for (const e of list) {
        if (!isObj(e) || !onlyMembers(e, ['priority', 'function', 'channels', 'firstResponseSeconds', 'resolutionSeconds']))
            return 'SLA_ENTRY_INVALID';
        if (!oneOf(CENTRAL_ROUTING_PRIORITIES, e.priority))
            return 'SLA_ENTRY_INVALID';
        if ('function' in e && !oneOf(CENTRAL_ROUTING_FUNCTIONS, e.function))
            return 'SLA_ENTRY_INVALID';
        if ('channels' in e && !distinctList(e.channels, (c) => oneOf(CENTRAL_ROUTING_CHANNELS, c), 1))
            return 'SLA_ENTRY_INVALID';
        if (!isIntIn(e.firstResponseSeconds, CENTRAL_ROUTING_BOUNDS.firstResponseSeconds) || !isIntIn(e.resolutionSeconds, CENTRAL_ROUTING_BOUNDS.resolutionSeconds))
            return 'SLA_ENTRY_INVALID';
        if (e.resolutionSeconds < e.firstResponseSeconds)
            return 'SLA_RESOLUTION_BEFORE_FIRST_RESPONSE';
        const id = `${e.priority}|${typeof e.function === 'string' ? e.function : ''}|${Array.isArray(e.channels) ? [...e.channels].sort().join(',') : ''}`;
        if (seen.has(id))
            return 'SLA_ENTRY_DUPLICATE';
        seen.add(id);
    }
    // Every priority has an entry that names neither a function nor a channel, so every contact has a target.
    for (const p of CENTRAL_ROUTING_PRIORITIES)
        if (!seen.has(`${p}||`))
            return 'SLA_PRIORITY_NOT_COVERED';
    return undefined;
}
/** The stable issue of a value that is not a routing rule set, or undefined when it is one. */
export function centralRoutingIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    if (!onlyMembers(v, ['rules', 'safety', 'fallbackFunction', 'overflowFunction', 'widening', 'reassign', 'capacity', 'sla']))
        return 'UNKNOWN_MEMBER';
    for (const m of ['rules', 'safety', 'fallbackFunction', 'overflowFunction', 'widening', 'reassign', 'capacity', 'sla'])
        if (!(m in v))
            return 'MISSING_MEMBER';
    if (!Array.isArray(v.rules) || v.rules.length > CENTRAL_ROUTING_BOUNDS.maxRules)
        return 'RULES_INVALID';
    const ids = new Set();
    for (const r of v.rules) {
        const issue = ruleIssue(r);
        if (issue !== undefined)
            return issue;
        const id = r.ruleId;
        if (ids.has(id))
            return 'RULE_ID_DUPLICATE';
        ids.add(id);
    }
    const safety = safetyIssue(v.safety);
    if (safety !== undefined)
        return safety;
    if (!oneOf(CENTRAL_ROUTING_FUNCTIONS, v.fallbackFunction) || !oneOf(CENTRAL_ROUTING_FUNCTIONS, v.overflowFunction))
        return 'FUNCTION_INVALID';
    // A queue the supervisors' team owns answers no channel: it can be neither where a contact lands nor where it overflows.
    if (v.fallbackFunction === 'supervisao' || v.overflowFunction === 'supervisao')
        return 'FUNCTION_ANSWERS_NO_CHANNEL';
    if (v.rules.some((r) => r.function === 'supervisao'))
        return 'FUNCTION_ANSWERS_NO_CHANNEL';
    const w = v.widening;
    if (!isObj(w) || !onlyMembers(w, ['toCountryAfterSeconds', 'toOverflowAfterSeconds']) || !isIntIn(w.toCountryAfterSeconds, CENTRAL_ROUTING_BOUNDS.widenSeconds) || !isIntIn(w.toOverflowAfterSeconds, CENTRAL_ROUTING_BOUNDS.widenSeconds))
        return 'WIDENING_INVALID';
    if (w.toOverflowAfterSeconds < w.toCountryAfterSeconds)
        return 'WIDENING_OVERFLOW_BEFORE_COUNTRY';
    const re = v.reassign;
    if (!isObj(re) || !onlyMembers(re, ['agentOfflineAfterSeconds', 'noReplyAfterSeconds']) || !isIntIn(re.agentOfflineAfterSeconds, CENTRAL_ROUTING_BOUNDS.agentOfflineAfterSeconds) || !isIntIn(re.noReplyAfterSeconds, CENTRAL_ROUTING_BOUNDS.noReplyAfterSeconds))
        return 'REASSIGN_INVALID';
    const cap = v.capacity;
    if (!isObj(cap) || !onlyMembers(cap, ['defaults', 'ceilings']) || !perChannel(cap.defaults, CENTRAL_ROUTING_BOUNDS.capacityDefault) || !perChannel(cap.ceilings, CENTRAL_ROUTING_BOUNDS.capacityCeiling))
        return 'CAPACITY_INVALID';
    if (CENTRAL_ROUTING_CHANNELS.some((c) => cap.defaults[c] > cap.ceilings[c]))
        return 'CAPACITY_DEFAULT_ABOVE_CEILING';
    return slaIssue(v.sla);
}
export function validCentralRoutingValue(v) {
    return centralRoutingIssue(v) === undefined;
}
/** Build a valid version (platform or country scope). Throws a RangeError on a malformed scope ref or value. */
export function centralRoutingSetting(input) {
    const issue = centralRoutingIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid Central routing rules: ${issue}`);
    return scopedSetting({ key: CENTRAL_ROUTING_KEY, scope: input.scope, ...(input.scopeRef !== undefined ? { scopeRef: input.scopeRef } : {}), value: input.value, version: input.version, effectiveFrom: input.effectiveFrom, ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}), mode: 'OVERRIDABLE' });
}
/**
 * The rule set in force for `country` (undefined = a conversation with no country: the platform's) at `at`, over the GOVERNED reader's
 * versions: the country's version, else the platform's, else the registry default. Never throws and never returns "no rules".
 */
export function resolveCentralRouting(settings, country, at) {
    const defaulted = (reasonCode, suffix) => ({ value: DEFAULT_CENTRAL_ROUTING, reasonCode, policyVersion: `${CENTRAL_ROUTING_KEY}@${suffix}` });
    if (country !== undefined && !COUNTRY_RE.test(country))
        return defaulted('CENTRAL_ROUTING_INVALID_DEFAULTED', 'invalid-defaulted');
    const bucket = (country !== undefined ? effectiveBucket(settings, CENTRAL_ROUTING_KEY, 'country', country, at) : undefined) ?? effectiveBucket(settings, CENTRAL_ROUTING_KEY, 'platform', undefined, at);
    if (bucket === undefined)
        return defaulted('CENTRAL_ROUTING_DEFAULT', 'default');
    if (!validCentralRoutingValue(bucket.value))
        return defaulted('CENTRAL_ROUTING_INVALID_DEFAULTED', 'invalid-defaulted');
    return {
        value: bucket.value,
        reasonCode: 'CENTRAL_ROUTING_GOVERNED',
        policyVersion: bucket.policyVersion,
        ...(bucket.contentHash !== undefined ? { contentHash: bucket.contentHash } : {}),
        ...(bucket.changeId !== undefined ? { changeId: bucket.changeId } : {}),
    };
}
//# sourceMappingURL=central-routing-policy.js.map